import { useTranslation } from "react-i18next";
import type { PendingInvite } from "@/lib/api";
import { roleLabel } from "@/lib/sharing/roleLabels";
import { inviteDateLabel } from "@/lib/sharing/pendingInvites";
import { testIds } from "@/testIds";

const T = testIds.sharing;

export interface PendingInviteRowProps {
  invite: PendingInvite;
  note?: string;
  onResend: () => void;
  onCopyLink: () => void;
  onCancel: () => void;
}

/**
 * An invitation not yet accepted (Olivia, 10 Oct, §c): a dashed ring where the
 * avatar will be, the address, "Can view · invited 3 Oct", and Resend · Copy
 * invite link · Cancel. Expired after 14 days: Resend · Cancel (its link no
 * longer works, so there is nothing to copy).
 */
export function PendingInviteRow({ invite, note, onResend, onCopyLink, onCancel }: PendingInviteRowProps) {
  const { t } = useTranslation("sharing");
  const expired = invite.status === "expired";
  const action = "rounded px-1 text-[11.5px] font-medium hover:bg-[var(--lilia-hover)]";
  return (
    <li
      data-testid={T.pendingInvite}
      data-invite-id={invite.id}
      data-status={invite.status}
      className="flex items-start gap-3 px-3 py-2.5"
    >
      <span
        aria-hidden="true"
        className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-[1.5px] border-dashed border-[var(--lilia-text-muted)] text-xs font-semibold text-[var(--lilia-text-muted)]"
      >
        {invite.email.charAt(0).toUpperCase()}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-[var(--lilia-text)]">{invite.email}</p>
        <p className={`truncate text-xs ${expired ? "text-[var(--lilia-warning,#8a5a00)]" : "text-[var(--lilia-text-muted)]"}`}>
          {roleLabel(invite.role)} · {inviteDateLabel(invite)}
        </p>
        <div className="-ml-1 mt-1 flex flex-wrap items-center gap-x-1 text-[var(--lilia-primary,#0a76a4)]">
          <button type="button" data-testid={T.pendingResend} className={action} onClick={onResend}>
            {t("pending.resend")}
          </button>
          {!expired && (
            <>
              <span aria-hidden="true" className="text-[var(--lilia-text-muted)]">·</span>
              <button type="button" data-testid={T.pendingCopyLink} className={action} onClick={onCopyLink}>
                {t("pending.copyLink")}
              </button>
            </>
          )}
          <span aria-hidden="true" className="text-[var(--lilia-text-muted)]">·</span>
          <button
            type="button"
            data-testid={T.pendingCancel}
            className={`${action} text-[var(--lilia-text-secondary)]`}
            onClick={onCancel}
            aria-label={t("pending.cancelLabel", { email: invite.email })}
          >
            {t("pending.cancel")}
          </button>
        </div>
        {note && (
          <p role="status" className="mt-1 text-[11.5px] text-[var(--lilia-text-secondary)]">
            {note}
          </p>
        )}
      </div>
    </li>
  );
}
